// @ts-nocheck
/**
 * The grid behind every Live Sheet tab except Teams.
 *
 * One table per tab, its own columns and its own edit rules. Nothing saves
 * until the director presses Save, and every write goes through the sheet-tab
 * edge function, which re-checks permission and re-applies the allowlist.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import SubmitRosterDialog from "@/components/events/SubmitRosterDialog";
import MediaReviewDialog from "@/components/events/sheet-tabs/MediaReviewDialog";
import ExtraDetailDialog from "@/components/events/sheet-tabs/ExtraDetailDialog";
import { TabColumn, TabDef } from "@/components/events/sheet-tabs/tabDefs";
import { getRlTeams } from "@/lib/rlTeams";
import { formatCalendarDate, formatTime } from "@/lib/dates";
import { displayHandle, cleanHandleInput } from "@/lib/handles";

const SUPABASE_URL = "https://kvttsygfutwcywroraji.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt2dHRzeWdmdXR3Y3l3cm9yYWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4MTU1OTUsImV4cCI6MjA4NjM5MTU5NX0.wUaZcZxKQpyk2DTzTF2CjYHvWRHa897xW0rleO6mJos";


const asMoney = (v: unknown, cents = false) => {
  if (v == null || v === "") return "";
  const n = Number(v) / (cents ? 100 : 1);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const HANDLE_FIELDS = new Set(["player_instagram_handle", "player_x_handle", "instagram_handle"]);
const fmtDate = (v: unknown) => (v ? formatCalendarDate(String(v), "M/d/yyyy") : "");

const CENTS_FIELDS = new Set(["amount_paid_cents", "processing_fee_cents"]);

const rowKeyOf = (row: any) => String(row.id ?? `reg:${row.registration_id}`);

export default function TabGrid({
  tab,
  eventId,
  eventName,
  shareToken,
  /** RecruitLook's answer for this event, keyed by team id. Rosters tab only. */
  rlRosters,
  rosterRule,
  rostersLoading,
  rostersError,
  onRetryRosters,
  onSubmitRoster,
  onShare,
  onOpenWindow,
  onCount,
  onNeedsGame,
}: {
  tab: TabDef;
  eventId: string;
  eventName?: string;
  shareToken?: string;
  rlRosters?: Record<string, any> | null;
  rosterRule?: { minimum: number; deadline: string | null };
  rostersLoading?: boolean;
  rostersError?: string | null;
  onRetryRosters?: () => void;
  onSubmitRoster?: (teamId: string, reason: string) => Promise<{ ok: boolean; error?: string }>;
  onShare?: () => void;
  onOpenWindow?: () => void;
  onCount?: (n: number) => void;
  /** Paid extras that want a game and haven't got one — shown on the tab. */
  onNeedsGame?: (n: number) => void;
}) {
  const [rows, setRows] = useState<any[]>([]);
  const [extra, setExtra] = useState<any>({});
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [ageFilter, setAgeFilter] = useState("all");
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});
  const [submitRow, setSubmitRow] = useState<any>(null);
  const [reviewRow, setReviewRow] = useState<any>(null);
  const [detailRow, setDetailRow] = useState<any>(null);
  const [rlNames, setRlNames] = useState<Record<string, string>>({});
  const [downloadingRosters, setDownloadingRosters] = useState(false);
  /** Extras tab only: which items want a game, and each team's games. */
  const [requiresGame, setRequiresGame] = useState<Record<string, boolean>>({});
  const [gamesByReg, setGamesByReg] = useState<Record<string, any[]>>({});
  const [regTeamNames, setRegTeamNames] = useState<Record<string, string>>({});
  const [gamesLoaded, setGamesLoaded] = useState(false);
  const [gamesMessage, setGamesMessage] = useState<string | null>(null);
  const [downloadingCallSheet, setDownloadingCallSheet] = useState(false);



  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
      body: { action: "load", tab: tab.key, event_id: eventId, share_token: shareToken },
    });
    setLoading(false);
    if (fnErr) {
      setError("This sheet couldn't be loaded. Try again in a moment.");
      return;
    }
    if (data?.denied) {
      setError(data.message);
      return;
    }
    if (data?.error) {
      setError(data.error);
      return;
    }
    const { rows: loaded = [], can_write, ...rest } = data ?? {};
    setRows(loaded);
    setExtra(rest);
    setCanWrite(Boolean(can_write) && !tab.readOnly);
    setEdits({});
    onCount?.(loaded.length);

    // RecruitLook owns team names. The Teams tab resolves them through the
    // batched client lookup; every other tab that shows a team does the same,
    // so a check-in sheet is never printed with dashes where names belong.
    const ids = [
      ...new Set(
        (loaded as any[])
          .map((r) => r.recruitlook_team_id || r.team_id)
          .filter(Boolean)
          .map(String)
      ),
    ];
    if (ids.length > 0) {
      try {
        const teams = await getRlTeams(ids);
        const names: Record<string, string> = {};
        for (const [id, t] of Object.entries(teams)) {
          const n = (t as any)?.display_name || (t as any)?.name || "";
          if (n) names[id] = n;
        }
        setRlNames(names);
      } catch (err) {
        // RL down: keep whatever the server sent rather than blanking the sheet.
        console.error("RecruitLook team lookup failed", err);
      }
    } else {
      setRlNames({});
    }
  }, [tab.key, tab.readOnly, eventId, shareToken]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The Extras tab needs three things the sheet-tab payload doesn't carry:
   * which items require a game, the games each team actually plays, and a name
   * for the opponent registration. All three are read-only lookups.
   */
  useEffect(() => {
    if (tab.key !== "extras" || shareToken || rows.length === 0) return;
    let cancelled = false;
    (async () => {
      setGamesMessage(null);
      // 1 · which extras want a game
      const { data: catalog } = await (supabase as any)
        .from("event_extras")
        .select("id, requires_game")
        .eq("event_id", eventId);
      if (cancelled) return;
      const req: Record<string, boolean> = {};
      (catalog ?? []).forEach((e: any) => {
        req[String(e.id)] = Boolean(e.requires_game);
      });
      setRequiresGame(req);

      // 2 · registration → team name, so an opponent reads as a team
      const { data: regs } = await (supabase as any)
        .from("event_registrations_v2")
        .select("id, recruitlook_team_id, team_name")
        .eq("event_id", eventId);
      if (cancelled) return;
      const names: Record<string, string> = {};
      (regs ?? []).forEach((r: any) => {
        if (r.team_name) names[String(r.id)] = r.team_name;
      });
      const rlIds = [
        ...new Set((regs ?? []).map((r: any) => r.recruitlook_team_id).filter(Boolean).map(String)),
      ];
      if (rlIds.length) {
        try {
          const teams = await getRlTeams(rlIds);
          (regs ?? []).forEach((r: any) => {
            const t = teams[String(r.recruitlook_team_id)];
            const n = (t as any)?.display_name || (t as any)?.name;
            if (n) names[String(r.id)] = n;
          });
        } catch {
          /* RL down — fall back to whatever names the registrations carry. */
        }
      }
      if (cancelled) return;
      setRegTeamNames(names);

      // 3 · each team's games, only for rows that want one
      const regIds = [
        ...new Set(
          rows
            .filter((r: any) => req[String(r.extra_id)] && r.registration_id)
            .map((r: any) => String(r.registration_id))
        ),
      ];
      const out: Record<string, any[]> = {};
      let denied: string | null = null;
      for (const id of regIds) {
        const { data, error: rpcErr } = await (supabase as any).rpc(
          "rle_games_for_registration",
          { p_registration_id: id }
        );
        if (rpcErr) {
          denied = rpcErr.message || "Couldn't load this event's schedule.";
          break;
        }
        out[id] = data ?? [];
      }
      if (cancelled) return;
      setGamesByReg(out);
      setGamesMessage(denied);
      setGamesLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [tab.key, shareToken, eventId, rows]);

  const gamesFor = (row: any) => gamesByReg[String(row?.registration_id ?? "")] ?? [];

  const wantsGame = (row: any) => Boolean(requiresGame[String(row?.extra_id ?? "")]);

  const isPaid = (row: any) =>
    ["paid", "free", "comped"].includes(String(row?.payment_status ?? "").toLowerCase());

  /** A paid shoot with no game is a camera operator with nowhere to stand. */
  const missingGame = (row: any) =>
    wantsGame(row) && isPaid(row) && !valueOf(row, "scheduled_game_id") && !row.game_date;

  const gameOptionLabel = (g: any) => {
    const opp = g.opponent_name || regTeamNames[String(g.opponent_registration_id ?? "")] || "";
    return opp ? `${g.label} · vs ${opp}` : g.label;
  };


  /** Rosters are RecruitLook's; RLE only shows where each one stands. */
  const displayRows = useMemo(() => {
    const withTeamName = (r: any) => {
      const id = String(r.recruitlook_team_id || r.team_id || "");
      const name =
        (id && rlNames[id]) ||
        r.team_name ||
        r.teams?.display_name ||
        r.teams?.canonical_name ||
        r.team_name_snapshot ||
        "";
      return name ? { ...r, team_name: name } : r;
    };

    if (tab.key !== "roster") return rows.map(withTeamName);
    const min = Math.max(1, rosterRule?.minimum ?? 6);
    const deadline = rosterRule?.deadline;
    const pastDeadline = Boolean(
      deadline && new Date(`${String(deadline).slice(0, 10)}T23:59:59`).getTime() < Date.now()
    );
    return rows.map((r: any) => {
      // Roster status is keyed by RecruitLook team id (the only team identity).
      const remote = rlRosters?.[String(r.recruitlook_team_id || r.team_id || "")];
      // null/undefined count = RecruitLook never reported one. Unknown is not zero.
      const count = typeof remote?.player_count === "number" ? remote.player_count : null;
      const known = count !== null;
      let state = "unknown";
      let label = "—";
      if (remote) {
        if (remote.locked) [state, label] = ["locked", "Locked"];
        else if (remote.submitted_at) [state, label] = ["submitted", "Submitted"];
        else if (!known) [state, label] = ["unknown", "Roster status unavailable"];
        else if (pastDeadline) [state, label] = ["overdue", `Overdue — ${count} of ${min}`];
        else if (count === 0) [state, label] = ["not_started", "Not started — 0 players"];
        else if ((count as number) < min) [state, label] = ["short", `${count} of ${min}`];
        else [state, label] = ["ready", "Ready"];
      }
      return {
        ...withTeamName(r),
        division: r.age_group,
        head_coach: [r.coach_first_name, r.coach_last_name].filter(Boolean).join(" "),
        players: known ? count : "",
        base_roster: min,
        roster_state: label,
        __roster_state: state,
        submitted_at: remote?.submitted_at ?? null,
        roster_url: remote?.roster_url ?? null,
        // Never nag a team to submit when we don't know what they have.
        __can_submit: Boolean(remote && known && (count as number) > 0 && !remote.submitted_at && !remote.locked),
        __players: count,
      };
    });
  }, [rows, tab.key, rlRosters, rosterRule, rlNames]);

  const ageGroups = useMemo(() => {
    const set = new Set<string>();
    displayRows.forEach((r: any) => r.division && set.add(String(r.division)));
    return Array.from(set).sort();
  }, [displayRows]);

  const valueOf = (row: any, id: string) => {
    const key = rowKeyOf(row);
    if (edits[key] && id in edits[key]) return edits[key][id];
    return row[id];
  };

  const visibleRows = useMemo(() => {
    let scoped = displayRows;
    if (tab.ageFilter && ageFilter !== "all") {
      scoped = scoped.filter((r: any) => String(r.division ?? "") === ageFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((r: any) =>
      tab.columns.some((c) => String(valueOf(r, c.id) ?? "").toLowerCase().includes(q))
    );
  }, [displayRows, search, ageFilter, tab, edits]);

  const needsGameCount = useMemo(
    () => (tab.key === "extras" ? displayRows.filter(missingGame).length : 0),
    [displayRows, tab.key, requiresGame, edits]
  );

  useEffect(() => {
    if (tab.key === "extras") onNeedsGame?.(needsGameCount);
  }, [needsGameCount, tab.key]);


  const dirtyCount = Object.values(edits).reduce((n, p) => n + Object.keys(p).length, 0);

  const setCell = (row: any, id: string, value: unknown) => {
    const key = rowKeyOf(row);
    setEdits((prev) => {
      const patch = { ...(prev[key] ?? {}) };
      if (String(row[id] ?? "") === String(value ?? "")) delete patch[id];
      else patch[id] = value;
      const next = { ...prev, [key]: patch };
      if (Object.keys(patch).length === 0) delete next[key];
      return next;
    });
  };

  const save = async () => {
    const updates = Object.entries(edits).map(([key, patch]) => {
      const row = displayRows.find((r: any) => rowKeyOf(r) === key);
      return { id: row?.id ?? null, key: row?.registration_id ?? null, patch };
    });
    if (updates.length === 0) return;
    setSaving(true);
    const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
      body: { action: "update", tab: tab.key, event_id: eventId, share_token: shareToken, updates },
    });
    setSaving(false);
    if (fnErr || data?.error) {
      toast.error(data?.error || "Couldn't save these changes.");
      return;
    }
    const failed = (data?.results ?? []).filter((r: any) => !r.ok);
    if (failed.length) toast.error(failed[0].error || "Some changes were rejected.");
    else toast.success(`Saved ${updates.length} row${updates.length === 1 ? "" : "s"}.`);
    await load();
  };

  const exportCsv = () => {
    const header = tab.columns.map((c) => c.title);
    const lines = visibleRows.map((r: any) =>
      tab.columns
        .map((c) => `"${String(valueOf(r, c.id) ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${eventName || "event"}-${tab.key}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Every player at the event, from RecruitLook, as one CSV.
   *
   * Rosters carry athletes' names, emails and phone numbers, so this is
   * signed-in-and-can-manage only — never offered behind a share link. Teams
   * that returned nothing are named out loud, because a short file that looks
   * complete is how a director arrives at check-in missing four teams.
   */
  const downloadRosters = async () => {
    setDownloadingRosters(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) {
        toast.error("Sign in to download rosters.");
        return;
      }
      const res = await fetch(`${SUPABASE_URL}/functions/v1/rle-roster-players`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_id: eventId }),
      });
      if (res.status === 403) {
        toast.error("You don't have permission to download rosters for this event.");
        return;
      }
      const out = await res.json().catch(() => null);
      if (!res.ok || !out?.ok) {
        toast.error(out?.error || "Couldn't download the rosters.");
        return;
      }
      const players = out.players ?? [];
      const missing = out.teams_without_players ?? [];
      if (players.length === 0) {
        toast.error("No rosters have been submitted for this event yet.");
        return;
      }
      const cols = [
        ["team_name", "Team"],
        ["division", "Division"],
        ["jersey_number", "Jersey"],
        ["first_name", "First name"],
        ["last_name", "Last name"],
        ["grad_year", "Grad year"],
        ["position", "Position"],
        ["email", "Email"],
        ["cell_phone", "Cell phone"],
        ["claimed", "Claimed"],
        ["invited", "Invited"],
        ["recruitlook_team_id", "RecruitLook team id"],
      ];
      const csv = [
        cols.map((c) => c[1]).join(","),
        ...players.map((p: any) =>
          cols
            .map((c) => `"${String(p[c[0]] ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${eventName || "event"}-rosters.csv`;
      a.click();
      URL.revokeObjectURL(url);

      if (missing.length) {
        const total = missing.length + new Set(players.map((p: any) => p.recruitlook_team_id)).size;
        toast.warning(
          `${missing.length} of ${total} teams have no roster yet: ${missing
            .slice(0, 4)
            .join(", ")}${missing.length > 4 ? `, +${missing.length - 4} more` : ""}`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Downloaded ${players.length} players.`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Couldn't download the rosters.");
    } finally {
      setDownloadingRosters(false);
    }
  };

  /**
   * What the media team works from on the day: every paid shoot in the order
   * it happens. Anything still missing a game goes at the top under its own
   * heading, because an omitted order is one nobody shoots.
   */
  const downloadCallSheet = async () => {
    setDownloadingCallSheet(true);
    try {
      const { data, error: rpcErr } = await (supabase as any).rpc("rle_media_call_sheet", {
        p_event_id: eventId,
      });
      if (rpcErr) {
        toast.error(rpcErr.message || "Couldn't build the call sheet.");
        return;
      }
      const all = (data ?? []) as any[];
      if (all.length === 0) {
        toast.error("Nothing to shoot yet — no paid media orders for this event.");
        return;
      }
      const cols: Array<[string, string]> = [
        ["game_date", "Date"],
        ["scheduled_start_time", "Start"],
        ["court_name", "Court"],
        ["game_label", "Game"],
        ["extra_name", "Product"],
        ["team_name", "Team"],
        ["division", "Division"],
        ["player_name", "Player"],
        ["player_jersey_number", "Jersey"],
        ["player_instagram_handle", "Instagram"],
        ["player_x_handle", "X"],
        ["buyer_email", "Buyer email"],
        ["fulfilment_status", "Fulfilment"],
      ];
      const q = (s: unknown) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      const line = (r: any) =>
        cols
          .map((c) => q(c[0] === "scheduled_start_time" ? formatTime(r[c[0]]) : r[c[0]]))
          .join(",");
      const header = cols.map((c) => q(c[1])).join(",");
      const missing = all.filter((r) => r.needs_game);
      const booked = all.filter((r) => !r.needs_game);
      const parts: string[] = [];
      if (missing.length) {
        parts.push(q(`NEEDS A GAME (${missing.length}) — assign these on the Extras tab`));
        parts.push(header, ...missing.map(line), "");
      }
      parts.push(q("CALL SHEET"), header, ...booked.map(line));
      const url = URL.createObjectURL(
        new Blob([parts.join("\n")], { type: "text/csv;charset=utf-8;" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${eventName || "event"}-media-call-sheet.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (missing.length) {
        toast.warning(`${missing.length} order${missing.length === 1 ? "" : "s"} still need a game.`);
      } else {
        toast.success(`Downloaded ${all.length} order${all.length === 1 ? "" : "s"}.`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Couldn't build the call sheet.");
    } finally {
      setDownloadingCallSheet(false);
    }
  };


  const review = async (decision: "approved" | "denied", reason: string) => {
    const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
      body: {
        action: "media_review",
        tab: "media",
        event_id: eventId,
        share_token: shareToken,
        id: reviewRow?.id,
        decision,
        reason: reason || null,
      },
    });
    if (fnErr || data?.error) return { ok: false, error: data?.error || "Couldn't record that." };
    await load();
    return { ok: true };
  };

  const renderCell = (row: any, col: TabColumn) => {
    const v = valueOf(row, col.id);
    const editable = canWrite && col.editable;

    /**
     * Game is a picker, never a typed date: a typed day can be a day the team
     * doesn't play, and a team plays several games a day anyway.
     */
    if (col.kind === "game") {
      if (!wantsGame(row)) return <span className="text-muted-foreground">—</span>;

      const games = gamesFor(row);
      const chip = missingGame(row) ? (
        <span className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
          Needs a game
        </span>
      ) : null;

      const current = String(v ?? "");
      const chosen = games.find((g: any) => String(g.game_id) === current);
      const shown =
        chosen ? gameOptionLabel(chosen) : row.game_label || fmtDate(row.game_date) || "";

      if (!editable) {
        return (
          <div className="flex flex-wrap items-center gap-2">
            <span>{shown || "—"}</span>
            {chip}
          </div>
        );
      }

      if (gamesMessage) {
        return (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">{gamesMessage}</span>
            {chip}
          </div>
        );
      }

      if (games.length === 0) {
        // No schedule yet — don't block anyone; the old free date still works.
        return (
          <div className="space-y-1">
            <span className="block text-xs text-muted-foreground">
              {gamesLoaded ? "No schedule yet for this event" : "Loading games…"}
            </span>
            <input
              type="date"
              value={row.game_date ? String(row.game_date).slice(0, 10) : ""}
              onChange={(e) => setCell(row, "game_date", e.target.value)}
              className="h-11 w-full rounded border border-input bg-background px-2 text-base sm:h-8 sm:text-sm"
            />
            {chip}
          </div>
        );
      }

      const draft = games.some((g: any) => g.schedule_status === "draft");
      return (
        <div className="space-y-1">
          {draft && (
            <span className="block text-xs text-muted-foreground">
              Draft schedule — these times may change.
            </span>
          )}
          <select
            value={current}
            onChange={(e) => setCell(row, col.id, e.target.value || null)}
            className="h-11 w-full rounded border border-input bg-background px-2 text-base sm:h-8 sm:text-sm"
          >
            <option value="">Pick a game…</option>
            {games.map((g: any) => (
              <option key={g.game_id} value={g.game_id}>
                {gameOptionLabel(g)}
              </option>
            ))}
          </select>
          {chip}
        </div>
      );
    }


    if (col.kind === "link") {
      return v ? (
        <a
          href={String(v)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }

    if (col.kind === "bool") {
      return (
        <input
          type="checkbox"
          checked={Boolean(v)}
          disabled={!editable}
          onChange={(e) => setCell(row, col.id, e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
      );
    }

    // Option lists may be plain strings or {value,label} pairs (fulfilment).
    const optionPairs: Array<{ value: string; label: string }> = (
      extra[col.optionsKey ?? ""] ?? []
    ).map((o: any) =>
      typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label }
    );

    if (!editable) {
      if (col.kind === "money") return <span>{asMoney(v, CENTS_FIELDS.has(col.id))}</span>;
      if (col.kind === "date") return <span>{fmtDate(v)}</span>;
      if (col.kind === "select") {
        const hit = optionPairs.find((o) => o.value === String(v ?? ""));
        return <span>{hit?.label ?? (v == null || v === "" ? "—" : String(v))}</span>;
      }
      if (HANDLE_FIELDS.has(col.id))
        return <span>{v == null || v === "" ? "—" : displayHandle(String(v))}</span>;
      return <span>{v == null || v === "" ? "—" : String(v)}</span>;
    }

    if (col.kind === "select") {
      return (
        <select
          value={String(v ?? "")}
          onChange={(e) => setCell(row, col.id, e.target.value)}
          className="h-8 w-full rounded border border-input bg-background px-1 text-sm"
        >
          <option value="">—</option>
          {optionPairs.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }


    return (
      <input
        type={col.kind === "number" ? "number" : col.kind === "date" ? "date" : "text"}
        value={
          col.kind === "date" && v ? String(v).slice(0, 10) : v == null ? "" : String(v)
        }
        onChange={(e) =>
          setCell(
            row,
            col.id,
            HANDLE_FIELDS.has(col.id) ? cleanHandleInput(e.target.value) : e.target.value
          )
        }
        className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
      />
    );
  };

  if (error) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm text-muted-foreground">{error}</div>
    );
  }

  // Tickets before anyone has configured any — a grid of nothing helps no one.
  if (tab.key === "tickets" && !loading && (extra.ticket_options ?? []).length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm">
        <p className="font-medium">No tickets configured for this event</p>
        <p className="mt-1 text-muted-foreground">
          Set up ticket types on the event before selling them.
        </p>
        {!shareToken && (
          <a
            href={`/admin-dashboard/events/${eventId}/edit?step=tickets`}
            className="mt-3 inline-flex text-primary underline"
          >
            Set up tickets
          </a>
        )}
      </div>
    );
  }

  // Tabs with nothing in the table at all read better as a sentence than a grid.
  if (tab.emptyMessage && !loading && rows.length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm">
        <p className="font-medium">{tab.emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
        />

        {tab.key === "roster" && (
          <>
            {rostersLoading && (
              <span className="text-xs text-muted-foreground">Checking rosters…</span>
            )}
            {!rostersLoading && rostersError && (
              <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                Roster status unavailable
                <button
                  type="button"
                  onClick={onRetryRosters}
                  className="rounded border border-input px-2 py-0.5 font-medium"
                >
                  Retry
                </button>
              </span>
            )}
          </>
        )}

        {canWrite && (
          <>
            <button
              type="button"
              onClick={save}
              disabled={saving || dirtyCount === 0}
              className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {saving ? "Saving…" : `Save changes${dirtyCount ? ` (${dirtyCount})` : ""}`}
            </button>
            <button
              type="button"
              onClick={() => setEdits({})}
              disabled={saving || dirtyCount === 0}
              className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium disabled:opacity-50"
            >
              Discard
            </button>
          </>
        )}

        {tab.key === "roster" && !shareToken && (
          <button
            type="button"
            onClick={downloadRosters}
            disabled={downloadingRosters}
            className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium disabled:opacity-50"
          >
            {downloadingRosters ? "Downloading…" : "Download rosters"}
          </button>
        )}

        <button
          type="button"
          onClick={exportCsv}
          className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium"
        >
          Export CSV
        </button>

        {tab.key === "extras" && !shareToken && (
          <button
            type="button"
            onClick={downloadCallSheet}
            disabled={downloadingCallSheet}
            className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium disabled:opacity-50"
          >
            {downloadingCallSheet ? "Building…" : "Download call sheet"}
          </button>
        )}

        {tab.key === "extras" && needsGameCount > 0 && (
          <span className="inline-flex h-9 items-center rounded-md bg-amber-100 px-3 text-sm font-semibold text-amber-900">
            {needsGameCount} need{needsGameCount === 1 ? "s" : ""} a game
          </span>
        )}



        {tab.printable && (
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        )}

        {!shareToken && onShare && (
          <button
            type="button"
            onClick={onShare}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        )}
        {!shareToken && onOpenWindow && (
          <button
            type="button"
            onClick={onOpenWindow}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <ExternalLink className="h-4 w-4" /> Open in new window
          </button>
        )}
        {loading && <span className="text-sm text-muted-foreground">Loading…</span>}
      </div>

      {tab.ageFilter && ageGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {["all", ...ageGroups].map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setAgeFilter(g)}
              className={`h-8 rounded-md border px-3 text-sm font-medium ${
                ageFilter === g
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background"
              }`}
            >
              {g === "all" ? "All divisions" : g}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr>
              {tab.columns.map((c) => (
                <th
                  key={c.id}
                  style={{ minWidth: c.width ?? 140 }}
                  className="border-b px-2 py-2 text-left font-semibold"
                >
                  {c.title}
                </th>
              ))}
              {(tab.key === "roster" || tab.key === "media" || tab.key === "extras") && (
                <th className="border-b px-2 py-2 text-left font-semibold print:hidden">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={tab.columns.length + 1}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  Nothing here yet.
                </td>
              </tr>
            )}
            {visibleRows.map((row: any) => (
              <tr key={rowKeyOf(row)} className="border-b last:border-0 hover:bg-muted/40">
                {tab.columns.map((c) => {
                  const opensDetail =
                    tab.key === "extras" && !(canWrite && c.editable);
                  return (
                    <td
                      key={c.id}
                      className={`px-2 py-1 align-middle ${opensDetail ? "cursor-pointer" : ""}`}
                      onClick={opensDetail ? () => setDetailRow(row) : undefined}
                    >
                      {renderCell(row, c)}
                    </td>
                  );
                })}
                {tab.key === "extras" && (
                  <td className="px-2 py-1 print:hidden">
                    <button
                      type="button"
                      onClick={() => setDetailRow(row)}
                      className="rounded border border-input px-2 py-1 text-xs font-semibold"
                    >
                      Open
                    </button>
                  </td>
                )}

                {tab.key === "roster" && (
                  <td className="px-2 py-1 print:hidden">
                    {row.__can_submit && canWriteRosterSubmit(shareToken) ? (
                      <button
                        type="button"
                        onClick={() => setSubmitRow(row)}
                        className="rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900"
                      >
                        Submit for them
                      </button>
                    ) : null}
                  </td>
                )}
                {tab.key === "media" && (
                  <td className="px-2 py-1 print:hidden">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => setReviewRow(row)}
                        className="rounded border border-input px-2 py-1 text-xs font-semibold"
                      >
                        Review
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SubmitRosterDialog
        open={submitRow !== null}
        onOpenChange={(o) => !o && setSubmitRow(null)}
        teamName={String(submitRow?.team_name ?? "")}
        playerCount={Number(submitRow?.__players ?? 0)}
        minimum={rosterRule?.minimum ?? 6}
        onSubmit={async (reason) => {
          const res = await onSubmitRoster?.(
            String(submitRow?.recruitlook_team_id ?? submitRow?.team_id ?? ""),
            reason,
          );
          if (res?.ok) toast.success("Roster submitted on the coach's behalf.");
          return res ?? { ok: false, error: "Roster submission isn't available here." };
        }}
      />

      <ExtraDetailDialog
        open={detailRow !== null}
        onOpenChange={(o) => !o && setDetailRow(null)}
        row={detailRow}
        canWrite={canWrite}
        onSave={async (id, patch) => {
          const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
            body: {
              action: "update",
              tab: tab.key,
              event_id: eventId,
              share_token: shareToken,
              updates: [{ id, key: null, patch }],
            },
          });
          const failed = (data?.results ?? []).find((r: any) => !r.ok);
          if (fnErr || data?.error || failed) {
            const msg = data?.error || failed?.error || "Couldn't save this purchase.";
            toast.error(msg);
            return { ok: false, error: msg };
          }
          toast.success("Purchase updated.");
          await load();
          return { ok: true };
        }}
      />

      <MediaReviewDialog
        open={reviewRow !== null}
        onOpenChange={(o) => !o && setReviewRow(null)}
        request={reviewRow}
        onDecide={review}
      />
    </div>
  );
}

/** Submitting on RecruitLook's side is never something a share link may do. */
const canWriteRosterSubmit = (shareToken?: string) => !shareToken;
